/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApWindowService } from '@ap/cdk';

import {
    Channel,
    Context,
    NBAOutcome,
    NextBestAction,
    PegaAssignments,
    PegaCDHContainerResponse,
    PegaNextBestActionBody,
    PegaPaginatedCasesResult
} from './pega.model';

@Injectable({ providedIn: 'root' })
export class PegaResource {
    private readonly mimeApplicationJson = 'application/json';
    private readonly baseUrl = '/api';
    private readonly headers = new HttpHeaders();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly windowService: ApWindowService
    ) {
        this.headers = this.headers.set('x-api-version', '1');
        this.headers = this.headers.set('Content-Type', this.mimeApplicationJson);
    }

    public static getCustomerID(theme: string, klantnummer: string): string {
        return `${this.getBrandIDusingBrand(theme)}_${klantnummer}`;
    }

    private static getBrandIDusingBrand(brand: string): number | undefined {
        switch (brand) {
            case 'sns':
                return 28;
            case 'blg':
                return 40;
            case 'rgb':
                return 230;
            case 'asn':
                return 270;
            default:
                return undefined;
        }
    }

    public getAssignments(): Observable<PegaAssignments> {
        let url = `${this.baseUrl}/case-mgmt/assignments`;
        // @ts-ignore
        if (this.windowService.getWindow()?.testautomation_env) {
            url = `${this.baseUrl}/case-mgmt-alt/assignments`;
        }
        return this.httpClient.get<PegaAssignments>(url, {
            headers: this.headers
        });
    }

    public getPaginatedCases(
        caseTypeId: string,
        personnelNumber?: string,
        officeId?: string,
        workerId?: string,
        customerId?: string,
        pageIndex = 1
    ) {
        let url = `${this.baseUrl}/case-mgmt/paginated-cases`;
        // @ts-ignore
        if (this.windowService.getWindow()?.testautomation_env) {
            url = `${this.baseUrl}/case-mgmt-alt/paginated-cases`;
        }
        let queryParams = new HttpParams().set('CaseTypeId', caseTypeId).set('PageIndex', `${pageIndex}`).set('case', 'camelCase');

        if (personnelNumber) {
            queryParams = queryParams.set('PersonnelNumber', personnelNumber);
        }
        if (officeId) {
            queryParams = queryParams.set('OfficeID', officeId);
        }
        if (workerId) {
            queryParams = queryParams.set('MedewerkerId', workerId);
        }
        if (customerId) {
            queryParams = queryParams.set('Klantnummer', customerId);
        }

        return this.httpClient.get<PegaPaginatedCasesResult>(url, {
            headers: this.headers,
            params: queryParams
        });
    }

    public getNextBestAction(
        customerId: string,
        containerName: string,
        contexts: Context[],
        channel?: Channel,
        userGuid?: string
    ): Observable<PegaCDHContainerResponse> {
        const headers = this.getHeaders();

        const url = `${this.baseUrl}/decisioning-hub/Container`;
        return this.httpClient.post<PegaCDHContainerResponse>(
            url,
            {
                ContainerName: containerName,
                Resource: 'Offers',
                CustomerID: customerId,
                ExternalID: userGuid,
                Channel: channel ? channel : Channel.WEB,
                Direction: 'Inbound',
                ContextName: 'Customer',
                Contexts: contexts
            } as PegaNextBestActionBody,
            {
                headers
            }
        );
    }

    public rejectNextBestAction(nba: NextBestAction, customerID: string, containerName: string): Observable<boolean> {
        return this.nextBestActionResponse(nba, customerID, containerName, NBAOutcome.REJECTED);
    }

    public nextBestActionResponse(
        nba: NextBestAction,
        customerID: string,
        containerName: string,
        outCome: NBAOutcome,
        channel?: Channel,
        userGuid?: string
    ): Observable<boolean> {
        const headers = this.getHeaders();
        const url = `${this.baseUrl}/decisioning-hub/CaptureResponse`;

        return this.httpClient.post<boolean>(
            url,
            {
                CustomerID: customerID,
                ExternalID: userGuid,
                ContainerName: containerName,
                RankedResults: [
                    {
                        Name: nba.name,
                        Issue: nba.issue,
                        Group: nba.group,
                        CampaignID: 'NBA',
                        InteractionID: nba.interactionID,
                        Outcome: outCome,
                        Behaviour: '',
                        Channel: channel ? channel : Channel.WEB,
                        Direction: 'Inbound',
                        Rank: nba.rank
                    }
                ]
            },
            { headers }
        );
    }

    private getHeaders(): HttpHeaders {
        let headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json; charset=UTF-8');
        headers = headers.set('accept', this.mimeApplicationJson);
        headers = headers.set('x-api-version', '3');

        return headers;
    }
}
